/**
 * Export/Import section for tasks user settings.
 * Uses the shared ExportImportSection with a custom selective export dialog
 * and third-party import links.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Download, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { ExportImportSection as SharedExportImportSection } from '../../../shared/components/common/ExportImportSection';
import { useNotification } from '../../../shared/context/NotificationContext';
import { useModuleExport, type ExportParams } from '../../../shared/hooks/useExportImport';
import { ExportSelectorDialog } from './ExportSelectorDialog';
import { toDateOnlyString } from '@/shared/utils/dateFormat';

interface TasksExportImportSectionProps {
  onNavigateAway?: () => void;
}

export function ExportImportSection({ onNavigateAway }: TasksExportImportSectionProps) {
  const { t } = useTranslation(['tasks', 'common']);
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [showExportSelector, setShowExportSelector] = useState(false);
  const flatExport = useModuleExport('tasks', 'export/flat');

  const handleFlatExport = async (params?: ExportParams) => {
    try {
      const blob = await flatExport.mutateAsync(params);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tasks_flat_export_${toDateOnlyString(new Date())}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showSuccess(t('tasks:exportImport.exportSuccess'));
    } catch {
      showError(t('tasks:exportImport.exportError'));
    }
  };

  const formatEntityCounts = (counts: Record<string, number>): string => {
    const parts: string[] = [];
    if (counts.workspaces) parts.push(`${counts.workspaces} ${t('tasks:exportImport.workspaces')}`);
    if (counts.boards) parts.push(`${counts.boards} ${t('tasks:exportImport.boards')}`);
    if (counts.items) parts.push(`${counts.items} ${t('tasks:exportImport.items')}`);
    return parts.join(', ');
  };

  return (
    <SharedExportImportSection
      moduleName="tasks"
      i18nNamespace="tasks"
      invalidateKeys={[['workspaces'], ['boards'], ['items']]}
      formatEntityCounts={formatEntityCounts}
      tagEntityType="tasks.task"
      exportAction={({ onExport, isPending }) => (
        <>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowExportSelector(true)}
            disabled={isPending}
          >
            {isPending ? (
              <Loader2 className="h-4 w-4 me-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 me-2" />
            )}
            {t('tasks:exportImport.exportButton')}
          </Button>

          <ExportSelectorDialog
            open={showExportSelector}
            onOpenChange={setShowExportSelector}
            onExport={(workspaceIds, boardIds, exportFormat) => {
              const params: ExportParams | undefined =
                workspaceIds.length || boardIds.length
                  ? { workspace_ids: workspaceIds, board_ids: boardIds }
                  : undefined;
              if (exportFormat === 'flat') {
                handleFlatExport(params);
              } else {
                onExport(params);
              }
              setShowExportSelector(false);
            }}
            isExporting={isPending || flatExport.isPending}
          />
        </>
      )}
    >
      {/* Third-Party Import Section */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium">
          {t('tasks:exportImport.thirdPartyTitle')}
        </h4>
        <p className="text-xs text-muted-foreground">
          {t('tasks:exportImport.thirdPartyDescription')}
        </p>

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              onNavigateAway?.();
              navigate('/tasks/import/notion');
            }}
          >
            <ExternalLink className="h-4 w-4 me-2" />
            {t('tasks:exportImport.importFromNotion')}
          </Button>
        </div>
      </div>
    </SharedExportImportSection>
  );
}
